import React from 'react';
import { useSeo } from '@/lib/seo';

const LAST_UPDATED = 'September 2, 2026';

// Replace these placeholders with the real details of the person
// who is legally responsible for operating CalculateEasy.
const LEGAL_NAME = 'Jose Torres';
const CONTACT_EMAIL = 'calculateeasytools@gmail.com';

const contentMap = {
  privacy: {
    title: 'Privacy Policy',
    path: '/privacy-policy',
    description: 'How CalculateEasy collects, uses, stores, and protects personal information.',
    body: (
      <>
        <p><strong>Last updated: {LAST_UPDATED}</strong></p>

        <p>
          CalculateEasy ("we", "us", or "our") operates this website.
          The person legally responsible for the website is <strong>{LEGAL_NAME}</strong>.
          You can contact us at <strong>{CONTACT_EMAIL}</strong>.
        </p>

        <h2>Information you provide</h2>
        <p>
          When you use our contact form, we may collect your name, email address,
          subject, and message. We use this information to respond to your request,
          communicate with you about your inquiry, and help prevent abuse of the service.
        </p>

        <h2>Information collected automatically</h2>
        <p>
          When you visit CalculateEasy, our hosting and security systems may process
          limited technical information such as IP address, browser and device
          information, referring URL, request information, and technical logs.
          This information may be used for security, abuse prevention, troubleshooting,
          and reliable operation of the website.
        </p>

        <p>
          CalculateEasy does not currently use a third-party analytics service to
          build behavioural profiles of visitors. We have integrated Google AdSense
          code while the website is reviewed for advertising. When that code loads,
          Google may process limited technical information needed to operate, secure,
          measure, and review its advertising services.
        </p>

        <h2>Contact form and service providers</h2>
        <p>
          Contact form submissions may be stored in our application database so that
          we can manage and respond to enquiries. We may also use service providers
          for hosting, database infrastructure, and email delivery. These providers
          process information only as needed to provide the services we use to operate
          CalculateEasy.
        </p>

        <h2>Local storage and preferences</h2>
        <p>
          CalculateEasy may use browser local storage to remember certain preferences,
          such as your selected currency. This is used to improve the functionality
          of the website and is not used by us to create advertising profiles.
        </p>

        <h2>Cookies and advertising</h2>
        <p>
          CalculateEasy uses Google AdSense code and is currently being reviewed for
          advertising. Ads may not be displayed while that review is in progress.
          If advertising is activated, Google and its advertising partners may use
          cookies, local storage, device identifiers, and similar technologies to
          deliver and measure ads, prevent fraud, limit repeated ads, and, where the
          user has consented, personalise advertising.
        </p>

        <p>
          Where consent is legally required, advertising that requires consent will
          be subject to choices presented through a Google-certified consent
          management platform. You can also manage stored data through your browser
          settings. Learn more about how Google uses information from sites that use
          its services in <a href="https://policies.google.com/technologies/partner-sites" className="text-emerald-700" target="_blank" rel="noreferrer">Google's partner-sites policy</a>.
        </p>

        <h2>How we use personal information</h2>
        <p>
          Personal information submitted through the contact form is used to respond
          to enquiries, operate and secure the service, prevent abuse, and comply with
          applicable legal obligations where relevant.
        </p>

        <h2>Data retention</h2>
        <p>
          We retain contact submissions only for as long as reasonably necessary to
          handle the enquiry, maintain appropriate records, prevent abuse, or comply
          with legal obligations.
        </p>

        <h2>Your rights</h2>
        <p>
          Depending on the applicable law and circumstances, you may have rights such
          as access, rectification, erasure, restriction of processing, objection,
          and data portability. You can contact us at <strong>{CONTACT_EMAIL}</strong>
          to exercise applicable rights or ask questions about your personal data.
        </p>

        <h2>Children</h2>
        <p>
          CalculateEasy is a general-audience website and is not specifically directed
          to children. We do not knowingly request personal information from children
          through our contact form. If you believe that a child has provided us with
          personal information, please contact us so that we can review and delete it
          where appropriate.
        </p>

        <h2>Security</h2>
        <p>
          We use reasonable technical and organisational measures designed to protect
          information against unauthorised access, misuse, alteration, or loss.
          However, no online service can guarantee absolute security.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          We may update this Privacy Policy when our services, processing activities,
          or legal requirements change. The latest version will be published on this page
          with its updated date.
        </p>

        <h2>Contact</h2>
        <p>
          For privacy questions or requests, please contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-700">
            {CONTACT_EMAIL}
          </a>.
          You can also use our <a href="/contact" className="text-emerald-700">Contact page</a>.
        </p>
      </>
    ),
  },

  cookie: {
    title: 'Cookie Policy',
    path: '/cookie-policy',
    description: 'How CalculateEasy uses cookies, local storage, and future advertising technologies.',
    body: (
      <>
        <p><strong>Last updated: {LAST_UPDATED}</strong></p>

        <h2>What are cookies?</h2>
        <p>
          Cookies are small files stored on your device by websites. CalculateEasy
          also uses browser local storage for certain preferences. Cookies and local
          storage are different technologies, so this policy describes them separately
          where appropriate.
        </p>

        <h2>What we use today</h2>
        <ul>
          <li>
            <strong>Preference storage:</strong> CalculateEasy may use browser local
            storage to remember preferences such as your selected currency.
          </li>
          <li>
            <strong>Essential technical storage:</strong> our hosting and application
            infrastructure may use technically necessary mechanisms required to keep
            the service secure and functioning.
          </li>
        </ul>

        <p>
          We do not currently use a third-party analytics platform. Google AdSense
          code has been added while the website is reviewed for advertising, so
          technical requests to Google may occur even when no advertisement is shown.
        </p>

        <h2>Google AdSense and advertising storage</h2>
        <p>
          If Google AdSense advertising is activated, Google and its advertising
          partners may use cookies, identifiers, or other local storage technologies
          for ad delivery, measurement, fraud prevention, frequency capping, and,
          where consent has been given, personalisation.
        </p>

        <p>
          Where required, consent choices will be presented through a Google-certified
          consent management platform before advertising that requires consent is
          served. You may withdraw or change your choices through the consent controls
          when available, and you can delete stored data in your browser settings.
          More information is available in <a href="https://policies.google.com/technologies/ads" className="text-emerald-700" target="_blank" rel="noreferrer">Google's advertising policy</a>.
        </p>

        <h2>Managing cookies and local storage</h2>
        <p>
          You can manage or delete cookies and local storage through your browser
          settings. Disabling non-essential storage should not prevent the calculators
          from performing their core functions, although some preferences may not be
          remembered.
        </p>

        <h2>Contact</h2>
        <p>
          If you have questions about this Cookie Policy, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-700">
            {CONTACT_EMAIL}
          </a>.
        </p>
      </>
    ),
  },

  terms: {
    title: 'Terms & Conditions',
    path: '/terms',
    description: 'The terms governing your use of CalculateEasy.',
    body: (
      <>
        <p><strong>Last updated: {LAST_UPDATED}</strong></p>

        <h2>1. Acceptable use</h2>
        <p>
          You may use CalculateEasy for lawful personal or business purposes.
          You must not attempt to disrupt the service, interfere with its security,
          submit malicious data, scrape the website at scale in a way that harms the
          service, or misrepresent our content as your own.
        </p>

        <h2>2. Calculators and general information</h2>
        <p>
          CalculateEasy provides calculators, tools, examples, and explanations for
          general informational purposes. Results should be independently checked
          before being relied upon for important decisions.
        </p>

        <h2>3. No professional advice</h2>
        <p>
          CalculateEasy is not a substitute for professional financial, tax, legal,
          medical, accounting, or engineering advice. Country-specific rules and
          circumstances may differ unless a tool expressly states otherwise.
        </p>

        <h2>4. Accuracy and availability</h2>
        <p>
          We take reasonable care to maintain accurate formulas and reliable tools,
          but we do not guarantee that every calculation will always be error-free,
          complete, or available without interruption.
        </p>

        <h2>5. Third-party services</h2>
        <p>
          Some features may depend on third-party infrastructure or services.
          Their availability and operation may be subject to their own terms and
          policies.
        </p>

        <h2>6. Intellectual property</h2>
        <p>
          The original content, page layouts, branding, and code of CalculateEasy
          are protected by applicable intellectual property laws. You may link to
          our pages, but you must not reproduce substantial portions of our original
          material without permission.
        </p>

        <h2>7. Changes</h2>
        <p>
          We may update these Terms & Conditions as the website develops or legal
          requirements change. Updated terms will be published on this page.
        </p>

        <h2>8. Contact</h2>
        <p>
          Questions about these Terms can be sent to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-700">
            {CONTACT_EMAIL}
          </a>.
        </p>
      </>
    ),
  },

  advertising: {
    title: 'Advertising Information',
    path: '/advertising',
    description: 'How Google AdSense advertising works on CalculateEasy and how we protect the user experience.',
    body: (
      <>
        <p><strong>Last updated: {LAST_UPDATED}</strong></p>

        <h2>Our approach</h2>
        <p>
          CalculateEasy is intended to remain useful and easy to navigate. Advertising
          is kept secondary to the main content and calculator tools.
        </p>

        <ul>
          <li>Advertising will be clearly distinguishable from editorial content and tools.</li>
          <li>Ads will not intentionally be placed where they could cause accidental clicks.</li>
          <li>Advertising will not be designed to imitate navigation or calculator controls.</li>
          <li>We will avoid intrusive layouts that interfere with the main user experience.</li>
        </ul>

        <h2>Ad partners</h2>
        <p>
          CalculateEasy has integrated Google AdSense code and the website is currently
          under review. If advertising is activated, Google and its advertising
          partners may use cookies, identifiers, or other technologies for ad delivery,
          measurement, fraud prevention, frequency capping, and personalisation where
          the user has consented.
        </p>

        <p>
          For users in the European Economic Area, the United Kingdom, and Switzerland,
          advertising that requires consent will be subject to choices provided through
          a Google-certified consent management platform.
        </p>

        <h2>Current status</h2>
        <p>
          The site is being reviewed by Google AdSense. AdSense code is present for
          verification, but ads may not yet be displayed.
        </p>

        <h2>Report an advertising issue</h2>
        <p>
          If you see an advertisement that appears
          inappropriate, misleading, or incorrectly placed, please contact us through
          our <a href="/contact" className="text-emerald-700">Contact page</a>.
        </p>
      </>
    ),
  },
};

export default function LegalPage({ kind }) {
  const c = contentMap[kind];

  useSeo({
    title: c.title,
    description: c.description,
    path: c.path,
  });

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
        {c.title}
      </h1>

      <div className="prose prose-slate mt-6 max-w-none text-slate-600 leading-relaxed [&_h2]:text-slate-900 [&_h2]:font-semibold [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_p]:mt-3">
        {c.body}
      </div>
    </article>
  );
}
